import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CancelConfirmationModal from './CancelConfirmationModal';

interface RequestCardProps {
  id: string;
  location: string;
  date: string;
  time?: string;
  status: 'pending' | 'approved' | 'cancelled' ;
  onCancel?: (id: string) => void;
  isCanceling?: boolean;
}

const RequestCard: React.FC<RequestCardProps> = ({
  id,
  location,
  date,
  time,
  status,
  onCancel,
  isCanceling = false
}) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-600';
      case 'rejected':
        return 'text-red-600';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleCancelClick = () => {
    setIsCancelModalOpen(true);
  };

  const handleCancelConfirm = () => {
    if (onCancel) {
      onCancel(id);
    }
    setIsCancelModalOpen(false);
  };

  const handleCancelClose = () => {
    setIsCancelModalOpen(false);
  };

  const handleInfoClick = () => {
    navigate(`/open-shift-details/${id}`);
  };

  return (
    <>
      <div className="mt-4 rounded-2xl border border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-[20px] leading-7 font-normal text-gray-800">{location}</p>
            <p className="mt-2 text-[16px] leading-6 text-gray-500">{date}</p>
            {time && (
              <p className="mt-3 text-[20px] leading-7 font-medium tracking-tight text-gray-800">{time}</p>
            )}
          </div>
          
          <div className="flex flex-col items-end space-y-2">
            <span className={`text-[16px] leading-6 font-normal ${getStatusColor(status)}`}>
              {getStatusText(status)}
            </span>
            
            {/* Info icon */}
            <button
              onClick={handleInfoClick}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="View Details"
            >
              <Info className="h-4 w-4 text-gray-500 hover:text-gray-700" />
            </button>
            
            {/* Show cancel link only for pending requests */}
            {status === 'pending' && onCancel && (
              <button
                onClick={handleCancelClick}
                className="text-[14px] leading-5 text-red-600 hover:text-red-700 font-medium underline hover:no-underline transition-all"
                disabled={isCanceling}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <CancelConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={handleCancelClose}
        onConfirm={handleCancelConfirm}
        shiftLocation={location}
        shiftDate={date}
        isLoading={isCanceling}
      />
    </>
  );
};

export default RequestCard;
